/**
 * The widget's own header row.
 *
 * The `context` slot is the frame every value below it is stated against — the
 * stay window, the quote's as-of, the corpus. A widget whose numbers float
 * free of their frame is a set of numbers, not an answer.
 *
 * The `leading` slot exists so the drill-back can REPLACE the set mark rather
 * than being added beside it. That is what keeps the top row one row tall in
 * both states.
 *
 * `bounds` is the second line, and it is the one slot here that WRAPS. What
 * bounds a projection — `3 of 18`, a page, a served window, a denominator that
 * belongs to one lane of three — is the class of fact a reader most needs and
 * the class a single truncating row is worst at holding: those strings are
 * long, they are several facts joined, and a tail clipped off `3 of 18 · no
 * sort applied` states the opposite of what it was written to state. So it sits
 * under the frame it qualifies rather than beside it, and it is allowed the
 * height it needs.
 *
 * Vendor handles do NOT come here. Lane names, credits, offsets and capture
 * counts go to the provenance disclosure at the foot, where a reader can reach
 * them without being made to read them.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import { useCresultcardFirstWordFloor } from "./cresultcard.first-word-floor";
import {
  CRC_ACCENT,
  CRC_DIVIDER,
  CRC_NUMERIC_STYLE,
  CRC_TEXT_FAINT,
  CRC_TRUNCATE_STYLE,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { CresultcardTextStep } from "./cresultcard.types";

const HEADER_GLYPH_SIZE = 14;
const HEADER_GLYPH_STROKE = 1.25;
const HEADER_ROW_GAP = 5;
const BOUNDS_FONT_SIZE = 10;
const HEADER_EDGE_TRACK = "auto";
const HEADER_CONTEXT_TRACK = "minmax(0, 1fr)";

interface CresultcardSetHeaderProps {
  readonly icon: TablerIcon;
  /** The pillar tag, e.g. `HOTELS`. */
  readonly label: string;
  readonly context?: ReactNode;
  /** What bounds the projection. The one slot that wraps. */
  readonly bounds?: ReactNode;
  /** A count, a rank, a freshness timestamp. */
  readonly trailing?: ReactNode;
  /** Replaces the mark. The drill-back takes this slot. */
  readonly leading?: ReactNode;
}

export function CresultcardSetHeader({
  icon: Glyph,
  label,
  context,
  bounds,
  trailing,
  leading,
}: CresultcardSetHeaderProps): ReactElement {
  const { first, rest, firstRef, floor } = useCresultcardFirstWordFloor(label);

  return (
    <header
      data-el="Cresultcard/SetHeader/Root"
      style={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        gap: HEADER_ROW_GAP,
        flexShrink: 0,
        minWidth: 0,
        paddingBlock: 11,
        paddingInline: 13,
        borderBottom: `1px solid ${CRC_DIVIDER}`,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: [
            HEADER_EDGE_TRACK,
            `minmax(${String(floor)}px, max-content)`,
            HEADER_CONTEXT_TRACK,
            HEADER_EDGE_TRACK,
          ].join(" "),
          alignItems: "center",
          gap: 9,
          minWidth: 0,
        }}
      >
        <span style={{ display: "inline-flex", gridColumn: 1 }}>
          {leading ?? (
            <Glyph
              color={CRC_ACCENT}
              size={HEADER_GLYPH_SIZE}
              stroke={HEADER_GLYPH_STROKE}
            />
          )}
        </span>
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Eyebrow],
            // The label YIELDS. A flex item's automatic minimum is its own
            // min-content width, so a label with no `minWidth: 0` cannot shrink
            // below its longest word and pushes the context and the trailing
            // slot out of the row instead; and without `nowrap` it wraps to a
            // second line, which is the other way this row stops being one row
            // tall. It ellipsises now, which is the only outcome that keeps both
            // promises.
            ...CRC_TRUNCATE_STYLE,
            gridColumn: 2,
          }}
        >
          <span ref={firstRef} style={{ whiteSpace: "nowrap" }}>
            {first}
          </span>
          {rest}
        </span>
        {context != null && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              ...CRC_NUMERIC_STYLE,
              ...CRC_TRUNCATE_STYLE,
              gridColumn: 3,
              marginInlineStart: 2,
            }}
          >
            {context}
          </span>
        )}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            gridColumn: 4,
            justifyContent: "flex-end",
          }}
        >
          {trailing}
        </span>
      </div>
      {bounds != null && (
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            // The one slot in this header that is allowed to wrap. A bound cut
            // in half states a different bound, so it takes the height instead.
            minWidth: 0,
            fontSize: BOUNDS_FONT_SIZE,
            color: CRC_TEXT_FAINT,
          }}
        >
          {bounds}
        </span>
      )}
    </header>
  );
}
